import React from 'react';
import KaraokeModificationFormView1 from './KaraokeModificationFormView1';
import KaraokeModificationFormView2 from './KaraokeModificationFormView2';
import KaraokeModificationFormView3 from './KaraokeModificationFormView3';
import KaraokeModificationFormView4 from './KaraokeModificationFormView4';
import KaraokeModificationFormConfirmNeedsRemakePopup from './KaraokeModificationFormConfirmNeedsRemakePopup';
import KaraokeModificationFormConfirmWithFeeBeforeDistributionPopup from './KaraokeModificationFormConfirmWithFeeBeforeDistributionPopup';
import { KaraokeModificationClientContext } from '../../context/KaraokeModificationClientContext';
import useCustomForm from '../../../common/hooks/CustomForm';
import { FieldValidationErrorsType, showErrorFlush } from '../../../common/form/FrekulForm';

export default function KaraokeModificationForm({
  karaokeOrderId,
  shouldModifyWithFee,
  shouldRemakeForModification,
  isDeliveredInJoysound,
  remakeFeeAmount,
  modificationFeeAmount,
} : {
  karaokeOrderId: number,
  isDeliveredInJoysound: boolean,
  shouldRemakeForModification: boolean,
  shouldModifyWithFee: boolean,
  remakeFeeAmount: number,
  modificationFeeAmount: number,
}) {
  type ViewName = 'view1'|'view2'|'view3'|'view4';
  type AjaxName = 'validate'|'execute';
  type PopupName = 'remake'|'confirm';
  const [view, setView] = React.useState<ViewName>(isDeliveredInJoysound ? 'view1' : 'view2');
  const [ajax, setAjax] = React.useState<AjaxName|null>(null);
  const [popup, setPopup] = React.useState<PopupName|null>(null);

  type FieldName = 'reason'|'correction'|'remake_url'|
  'modification_item1'|'modification_item2'|'modification_item3'|
  'modification_details1'|'modification_details2'|'modification_details3';
  type ValuesType = { [name in FieldName]: string };
  type ErrorsType = { [name in FieldName]? : FieldValidationErrorsType };

  const [values, errors, setValues, setErrors] = useCustomForm<ValuesType, ErrorsType, { name : FieldName }>({
    defaultValue: {
      // JOYSOUND配信済みの場合は「お客様都合」「Frekulの過失」を選べる。
      // それ以外の場合は常に「お客様都合」となる。
      // ただし、未配信の場合でもまだ修正不可の状態がある。
      // この場合、とりあえず選択画面を出すがdisableになっている、という状態にする。
      reason: isDeliveredInJoysound ? '' : 'modification',
      correction: '',
      modification_item1: '',
      modification_item2: '',
      modification_item3: '',
      modification_details1: '',
      modification_details2: '',
      modification_details3: '',
      remake_url: '',
    }
  });

  const {
    validateKaraokeModification,
    executeKaraokeModification,
  } = React.useContext(KaraokeModificationClientContext);

  React.useEffect(() => {
    if (ajax === 'validate') {
      validateKaraokeModification(karaokeOrderId, values).then((responseData) => {
        setAjax(null);
        setErrors(responseData.validationErrors || {});
        if (responseData.result) {
          setView('view3');
        } else {
          showErrorFlush('入力内容に不備があります。');
        }
      });
    } else if (ajax === 'execute') {
      executeKaraokeModification(karaokeOrderId, values).then((responseData) => {
        setAjax(null);
        setErrors(responseData.validationErrors || {});
        if (responseData.result) {
          setView('view4');
        } else {
          showErrorFlush('入力内容に不備があります。');
        }
      });
    }
  }, [ajax]);

  const handleBackToHome = () => {
    window.location.href = '/';
  };

  let amount = 0;
  if (values.reason === 'modification') {
    if (shouldRemakeForModification) {
      amount = remakeFeeAmount;
    } else if (shouldModifyWithFee) {
      amount = modificationFeeAmount;
    }
  }

  return (
    <>
      {view === 'view1' && (
        <KaraokeModificationFormView1
          getValue={(name) => values[name]}
          setValue={(name, value) => setValues({ ...values, [name]: value }, { name })}
          getErrors={(name) => errors?.[name] || {}}
          onNext={() => {
            if (values.reason === 'modification') {
              if (shouldRemakeForModification) {
                setPopup('remake');
              } else if (shouldModifyWithFee) {
                setPopup('confirm');
              } else {
                setView('view2');
              }
            } else {
              setView('view2');
            }
          }}
        />
      )}
      {view === 'view2' && (
        <KaraokeModificationFormView2
          getValue={(name) => values[name]}
          setValue={(name, value) => setValues({ ...values, [name]: value }, { name })}
          getErrors={(name) => errors?.[name] || {}}
          shouldRemakeForModification={shouldRemakeForModification}
          onNext={() => setAjax('validate')}
        />
      )}
      {view === 'view3' && (
        <KaraokeModificationFormView3
          getValue={(name) => values[name]}
          amount={amount}
          shouldRemakeForModification={shouldRemakeForModification}
          onBack={() => setView('view2')}
          onNext={() => setAjax('execute')}
        />
      )}
      {view === 'view4' && (
        <KaraokeModificationFormView4
          amount={amount}
          paymentUrl={shouldRemakeForModification ? 'https://buy.stripe.com/fZe9Bb72G1xO0k8aER' : 'https://buy.stripe.com/eVa00B4Uy7Wc9UI3cf'}
          onBackToHome={() => handleBackToHome()}
        />
      )}
      {popup === 'confirm' && (
        <KaraokeModificationFormConfirmWithFeeBeforeDistributionPopup
          onClose={() => {
            setPopup(null);
          }}
          onBack={() => handleBackToHome()}
          onNext={() => {
            setPopup(null);
            setView('view2');
          }}
          modificationFeeAmount={modificationFeeAmount}
        />
      )}
      {popup === 'remake' && (
        <KaraokeModificationFormConfirmNeedsRemakePopup
          onClose={() => {
            setPopup(null);
          }}
          onBack={() => handleBackToHome()}
          onNext={() => {
            setPopup(null);
            setView('view2');
          }}
          remakeFeeAmount={remakeFeeAmount}
        />
      )}
    </>
  );
}
